import type { ComputePositionConfig } from '@floating-ui/react';
import { PluginKey } from '@tiptap/pm/state';
import type { Editor } from '@tiptap/react';
import { Link, Trash2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { DragHandle } from '../drag-handle-react/DragHandle';

interface NodeInfo {
    node: any;
    editor: Editor;
    pos: number;
}

interface DragHandlerProps {
    editor: Editor;
    actions?: (nodeInfo: NodeInfo) => React.ReactNode;
    onNodeChange?: (nodeInfo: NodeInfo) => void;
    showDelete?: boolean;
}

const computePositionConfig: ComputePositionConfig = { strategy: 'absolute', placement: 'right-start' };

const floatingMenuPluginKey = new PluginKey('floating-menu');

type ReferenceContext = {
    sectionId: string | null;
    currentNodeId: string | null;
    resourceId: string | null;
    resourcePos: number | null;
    resourceTypeName: string | null;
};

function getReferenceContext(nodeInfo: NodeInfo): ReferenceContext {
    const sectionId = nodeInfo.editor.state.doc.attrs?.sectionId;
    const currentNodeId = nodeInfo.node?.attrs?.id;

    const sectionIdValue = typeof sectionId === 'string' && sectionId.length > 0 ? sectionId : null;
    const currentNodeIdValue = typeof currentNodeId === 'string' && currentNodeId.length > 0 ? currentNodeId : null;

    // Validate position is within valid range
    if (nodeInfo.pos < 0 || nodeInfo.pos > nodeInfo.editor.state.doc.content.size) {
        return {
            sectionId: sectionIdValue,
            currentNodeId: currentNodeIdValue,
            resourceId: null,
            resourcePos: null,
            resourceTypeName: null,
        };
    }

    const probePos = nodeInfo.pos < nodeInfo.editor.state.doc.content.size ? nodeInfo.pos + 1 : nodeInfo.pos;
    const $pos = nodeInfo.editor.state.doc.resolve(probePos);
    let topMostParentId: string | null = null;
    let topMostParentPos: number | null = null;
    let topMostParentTypeName: string | null = null;

    for (let depth = 1; depth <= $pos.depth; depth += 1) {
        const ancestorNode = $pos.node(depth);
        const ancestorId = ancestorNode.attrs?.id;
        if (typeof ancestorId === 'string' && ancestorId.length > 0) {
            topMostParentId = ancestorId;
            topMostParentPos = $pos.before(depth);
            topMostParentTypeName = ancestorNode.type.name;
            break;
        }
    }

    const resolvedResourceId = topMostParentId ?? currentNodeIdValue;
    const resourceId = typeof resolvedResourceId === 'string' && resolvedResourceId.length > 0 ? resolvedResourceId : null;

    let resourcePos = topMostParentPos;
    let resourceTypeName = topMostParentTypeName;
    if (resourcePos == null && nodeInfo.node) {
        resourcePos = nodeInfo.pos;
        resourceTypeName = nodeInfo.node.type?.name ?? null;
    }

    return {
        sectionId: sectionIdValue,
        currentNodeId: currentNodeIdValue,
        resourceId,
        resourcePos,
        resourceTypeName,
    };
}

function ensureResourceId(nodeInfo: NodeInfo, ctx: ReferenceContext): string | null {
    if (ctx.resourceId) {
        return ctx.resourceId;
    }

    if (ctx.resourcePos == null || ctx.resourceTypeName !== 'zyExample') {
        return null;
    }

    const resourceNode = nodeInfo.editor.state.doc.nodeAt(ctx.resourcePos);
    if (!resourceNode || resourceNode.type.name !== 'zyExample') {
        return null;
    }

    const generatedId = uuidv4();
    const tr = nodeInfo.editor.state.tr;
    tr.setNodeMarkup(ctx.resourcePos, undefined, {
        ...resourceNode.attrs,
        id: generatedId,
    });
    nodeInfo.editor.view.dispatch(tr);
    return generatedId;
}

function buildReferenceToken(nodeInfo: NodeInfo): string | null {
    const ctx = getReferenceContext(nodeInfo);
    if (!ctx.sectionId) {
        return null;
    }

    const resourceId = ensureResourceId(nodeInfo, ctx);
    if (!resourceId) {
        return null;
    }
    const isNestedInlineEnum = nodeInfo.node?.type?.name === 'zyInlineEnum' && ctx.currentNodeId && resourceId !== ctx.currentNodeId;

    if (isNestedInlineEnum) {
        return `reference:${ctx.sectionId}#${resourceId}#${ctx.currentNodeId}#`;
    }

    return `reference:${ctx.sectionId}#${resourceId}#`;
}

export function ZyFloatingMenu({ editor, onNodeChange }: DragHandlerProps) {
    const [nodeInfo, setNodeInfo] = useState<NodeInfo | null>(null);

    const handleNodeChange = useCallback(
        (info: NodeInfo) => {
            setNodeInfo(info);
            onNodeChange?.(info);
        },
        [onNodeChange],
    );

    const handleDelete = useCallback(() => {
        if (!nodeInfo) {
            console.error('Error deleting node: No node info found');
            return;
        }
        const { editor, pos, node } = nodeInfo;
        editor.commands.deleteRange({ from: pos, to: pos + node.nodeSize });
    }, [nodeInfo]);

    const handleCopyReference = useCallback(async () => {
        if (!nodeInfo) {
            console.error('Error copying reference: No node info found');
            return;
        }
        const token = buildReferenceToken(nodeInfo);
        if (!token) {
            console.error('Error copying reference: Missing section or resource ID');
            toast.error('Copy failed', {
                description: 'Unable to copy resource reference.',
            });
            return;
        }

        try {
            await navigator.clipboard.writeText(token);
            toast.success('Resource reference copied', {
                description: nodeInfo.node.attrs.caption
                    ? `Reference to "${nodeInfo.node.attrs.caption}" has been copied to clipboard`
                    : '',
            });
        } catch (error) {
            console.error('Error copying reference to clipboard:', error);
            toast.error('Copy failed', {
                description: 'Unable to copy resource reference.',
            });
        }
    }, [nodeInfo]);

    const showMenu = nodeInfo?.node?.type?.spec?.showFloatingMenu || false;
    const canCopyReference = nodeInfo
        ? (() => {
              const ctx = getReferenceContext(nodeInfo);
              return Boolean(ctx.sectionId && (ctx.resourceId || ctx.resourceTypeName === 'zyExample'));
          })()
        : false;

    return (
        <DragHandle
            className="pl-3 w-12 flex flex-col items-end"
            pluginKey={floatingMenuPluginKey}
            editor={editor}
            onNodeChange={handleNodeChange}
            computePositionConfig={computePositionConfig}
            nested
        >
            <div
                className={`flex flex-col w-8 rounded border border-gray-300 bg-white shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700 ${!showMenu ? 'hidden' : ''}`}
            >
                {/* Delete Button */}
                <button
                    type="button"
                    onClick={handleDelete}
                    className="h-8 flex items-center justify-center text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
                    title="Delete block"
                >
                    <Trash2 className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={() => {
                        void handleCopyReference();
                    }}
                    className="h-8 flex items-center justify-center text-gray-500 hover:text-blue-500 disabled:text-gray-300 disabled:hover:text-gray-300 dark:text-gray-400 dark:hover:text-blue-400 dark:disabled:text-gray-600"
                    title="Copy resource reference"
                    disabled={!canCopyReference}
                >
                    <Link className="h-4 w-4" />
                </button>
            </div>
        </DragHandle>
    );
}

export default ZyFloatingMenu;
